import { useEffect, useState, useCallback } from 'react'
import { MapContainer, TileLayer, CircleMarker, Popup, LayerGroup } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { Globe, Plus, X, Trash2, RefreshCw, AlertTriangle } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import MapLegend from '../components/MapLegend'

interface Entry {
  id: number
  service: string
  provider: string
  region: string
  country: string
  lat: number
  lng: number
  classification: string
  regulatory_basis: string
  notes: string
}

interface Risk {
  service: string
  country: string
  classification: string
  basis: string
  reason: string
}

const CLS_TONE: Record<string, string> = {
  public:       '#10b981',
  internal:     '#3b82f6',
  confidential: '#f59e0b',
  restricted:   '#f43f5e',
  regulated:    '#a855f7',
}

const PROVIDERS = ['aws', 'azure', 'gcp', 'on-prem', 'other']
const CLASSES = ['public', 'internal', 'confidential', 'restricted', 'regulated']

export default function DataResidencyPage() {
  const [data, setData] = useState<{ entries: Entry[]; risk_flags: Risk[]; by_country: Record<string, number>; by_classification: Record<string, number>; by_provider: Record<string, number> } | null>(null)
  const [regions, setRegions] = useState<{ id: string; lat: number; lng: number; country: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    service: '', provider: 'aws', region: '', country: '',
    lat: 0, lng: 0, classification: 'internal',
    regulatory_basis: '', notes: '',
  })

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [a, b] = await Promise.all([
        api.get('/api/overlay/data-residency'),
        api.get('/api/overlay/data-residency/regions'),
      ])
      setData(a.data)
      setRegions(b.data?.regions || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // When user picks a region, autofill lat/lng/country
  const setRegion = (rid: string) => {
    const r = regions.find(x => x.id === rid)
    setForm({ ...form, region: rid, ...(r ? { lat: r.lat, lng: r.lng, country: r.country } : {}) })
  }

  const create = async () => {
    if (!form.service) return
    await api.post('/api/overlay/data-residency', form)
    setShowForm(false)
    setForm({ ...form, service: '', region: '', notes: '' })
    fetchAll()
  }

  const remove = async (id: number) => {
    if (!confirm('Delete entry?')) return
    await api.delete(`/api/overlay/data-residency/${id}`)
    fetchAll()
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Globe size={24} className="text-blue-light" /> Data Residency Map
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Per-service "where the data lives". Risk flags fire when restricted/regulated data sits outside EU/UK.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs"><RefreshCw size={11} className={loading ? 'animate-spin' : ''} /></button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs"><Plus size={12} /> Add service</button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Tile label="Services" value={data.entries.length} tone="blue" />
          <Tile label="Countries" value={Object.keys(data.by_country).length} tone="emerald" />
          <Tile label="Restricted" value={data.by_classification.restricted || 0} tone="rose" />
          <Tile label="Regulated"  value={data.by_classification.regulated  || 0} tone="amber" />
          <Tile label="Risk flags" value={data.risk_flags.length} tone={data.risk_flags.length > 0 ? 'rose' : 'emerald'} pulse={data.risk_flags.length > 0} />
        </div>
      )}

      {data?.risk_flags && data.risk_flags.length > 0 && (
        <div className="card p-3 border border-rose/30 bg-rose/5">
          <h3 className="text-sm font-semibold text-rose-light mb-2 flex items-center gap-2">
            <AlertTriangle size={14} /> Sovereignty risk flags
          </h3>
          <ul className="text-[11px] text-text-secondary space-y-1">
            {data.risk_flags.map((r, i) => (
              <li key={i}>· <code className="font-mono text-rose">{r.service}</code> — {r.reason}{r.basis && <> · basis: {r.basis}</>}</li>
            ))}
          </ul>
        </div>
      )}

      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Add service residency</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={form.service} onChange={e => setForm({...form, service: e.target.value})} placeholder="Service" className="input py-1.5" />
            <select value={form.provider} onChange={e => setForm({...form, provider: e.target.value})} className="input py-1.5">
              {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={form.region} onChange={e => setRegion(e.target.value)} className="input py-1.5">
              <option value="">— pick region —</option>
              {regions.map(r => <option key={r.id} value={r.id}>{r.id} ({r.country})</option>)}
            </select>
            <input value={form.country} onChange={e => setForm({...form, country: e.target.value})} placeholder="Country (ISO-2)" className="input py-1.5 font-mono" maxLength={4} />
            <select value={form.classification} onChange={e => setForm({...form, classification: e.target.value})} className="input py-1.5">
              {CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input value={form.regulatory_basis} onChange={e => setForm({...form, regulatory_basis: e.target.value})} placeholder="Regulatory basis (GDPR / UK-GDPR / FCA / PCI…)" className="input py-1.5" />
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} placeholder="Notes" className="input py-1.5 md:col-span-2" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={create} className="btn-primary text-xs">Save</button>
          </div>
        </div>
      )}

      {/* Map */}
      <div className="card p-0 overflow-hidden relative" style={{ height: 480 }}>
        <MapLegend
          floating
          corner="bottom-left"
          title="Classification"
          entries={[
            { hex: CLS_TONE.public,       label: 'Public' },
            { hex: CLS_TONE.internal,     label: 'Internal' },
            { hex: CLS_TONE.confidential, label: 'Confidential' },
            { hex: CLS_TONE.restricted,   label: 'Restricted' },
            { hex: CLS_TONE.regulated,    label: 'Regulated' },
          ]}
        />
        <MapContainer center={[20, 0]} zoom={2} minZoom={2} style={{ height: '100%', width: '100%', background: '#0b1020' }}>
          <TileLayer url="/api/tiles/{z}/{x}/{y}.png" attribution='&copy; CARTO (proxied)' />
          <LayerGroup>
            {(data?.entries || []).filter(e => e.lat && e.lng).map(e => (
              <CircleMarker key={e.id} center={[e.lat, e.lng]}
                radius={e.classification === 'restricted' || e.classification === 'regulated' ? 9 : 6}
                pathOptions={{
                  color: CLS_TONE[e.classification] || '#3b82f6',
                  fillColor: CLS_TONE[e.classification] || '#3b82f6',
                  fillOpacity: 0.6, weight: 2,
                }}>
                <Popup>
                  <div className="text-xs">
                    <strong>{e.service}</strong><br/>
                    {e.provider} · {e.region} · {e.country}<br/>
                    <span style={{ color: CLS_TONE[e.classification] }}>● {e.classification}</span>
                    {e.regulatory_basis && <><br/><em>{e.regulatory_basis}</em></>}
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </LayerGroup>
        </MapContainer>
      </div>

      {/* Table */}
      {data?.entries && data.entries.length > 0 && (
        <div className="card p-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                <th className="text-left py-2 px-2">Service</th>
                <th className="text-left py-2 px-2">Provider</th>
                <th className="text-left py-2 px-2">Region</th>
                <th className="text-left py-2 px-2">Country</th>
                <th className="text-left py-2 px-2">Classification</th>
                <th className="text-left py-2 px-2">Basis</th>
                <th className="text-right py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map(e => (
                <tr key={e.id} className="border-b border-border/50 hover:bg-surface-hover">
                  <td className="py-1.5 px-2 text-text-primary font-medium">{e.service}</td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-text-muted">{e.provider}</td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-text-secondary">{e.region}</td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-text-primary">{e.country}</td>
                  <td className="py-1.5 px-2">
                    <span className="badge text-[9px] uppercase border" style={{ color: CLS_TONE[e.classification], borderColor: CLS_TONE[e.classification] }}>
                      {e.classification}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 text-[10px] text-text-muted">{e.regulatory_basis}</td>
                  <td className="py-1.5 px-2 text-right">
                    <button type="button" onClick={() => remove(e.id)} className="text-text-muted hover:text-rose"><Trash2 size={11} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Tile({ label, value, tone, pulse }: { label: string; value: number; tone: string; pulse?: boolean }) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald' : 'text-blue-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', t, pulse && 'animate-pulse')}>{value}</div>
    </div>
  )
}
